/**
 * Geolocation Configuration Constants
 *
 * Centralized GPS configuration constants to eliminate magic numbers
 * and ensure consistency across the application.
 *
 * @module constants/geolocation
 */

/**
 * GPS request timeout in milliseconds
 * Maximum time to wait for browser to provide GPS coordinates
 */
export const GEOLOCATION_TIMEOUT_MS = 10000; // 10 seconds

/**
 * Maximum age for cached GPS position in milliseconds (extended cache)
 * Used for task creation where slightly stale location is acceptable
 */
export const GEOLOCATION_MAX_AGE_LONG_MS = 300000; // 5 minutes

/**
 * Maximum age for cached GPS position in milliseconds (short cache)
 * Used for realtime tracking where fresh location is required
 */
export const GEOLOCATION_MAX_AGE_SHORT_MS = 60000; // 1 minute

/**
 * Enable high-accuracy GPS mode
 * Uses GPS hardware instead of IP-based location
 */
export const GEOLOCATION_HIGH_ACCURACY = true;

/**
 * Maximum distance in meters to consider coordinate "on" a road
 * Used for GPS-based road detection
 */
export const GPS_ROAD_PROXIMITY_THRESHOLD_M = 100;

/**
 * Minimum GPS coordinate change in meters to trigger update
 * Prevents excessive updates for minor position changes
 */
export const GPS_COORDINATE_CHANGE_THRESHOLD_M = 10;

/** Permission state reported when the user has granted geolocation access. */
export const PERMISSION_GRANTED: PermissionState = 'granted';

/** Permission state reported when the user has denied geolocation access. */
export const PERMISSION_DENIED: PermissionState = 'denied';

/** Minimum valid WGS84 latitude in degrees. */
export const MIN_LATITUDE = -90;

/** Maximum valid WGS84 latitude in degrees. */
export const MAX_LATITUDE = 90;

/** Minimum valid WGS84 longitude in degrees. */
export const MIN_LONGITUDE = -180;

/** Maximum valid WGS84 longitude in degrees. */
export const MAX_LONGITUDE = 180;
